import React, { useState, useEffect, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { ChevronDown, ChevronRight, Bot, CornerDownRight } from "lucide-react";
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DefaultToolCallRenderer } from "../ToolRenderers";
import { ChatMessageStream } from "../ChatMessageStream";

// Message interface for the SubAgentToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// SubAgentToolCall component
function SubAgentToolCall({ message, chatIdRef }: { message: Message, chatIdRef: React.RefObject<string | null> }) {
    const [isOpen, setIsOpen] = useState(false);
    // Parse the task from the function args
    let task = "Sub-agent task";
    if (message.tool_call_function_args) {
        try {
            const args = JSON.parse(message.tool_call_function_args);
            if (args && args.task) {
                task = args.task;
            }
        } catch (error) {
            console.error("Error parsing tool call args:", error);
        }
    }

    // Format the agent name for display
    let agentDisplayName = "Sub Agent";
    if (message.tool_call_function_name) {
        agentDisplayName = message.tool_call_function_name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') + " Sub Agent";
    }

    return (
        <div className="space-y-4 w-full">
            {/* Content section with subtle border */}
            <div className="">
                <div className="text-textmedium">
                    <MarkdownRenderer content={message.content || `Running ${agentDisplayName} with task: ${task}`} />
                </div>
            </div>

            {/* Agent header with icon */}
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-primary font-medium">{"Calling " + agentDisplayName}</span>
                    <span className="text-blue-500 text-xs px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20">Running</span>
                </div>
            </div>
            {/* Collapsible section for sub-agent conversation */}
            {chatIdRef.current && (
                <div className="mt-4">
                    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
                        <CollapsibleTrigger
                            className="flex items-center gap-2 text-sm text-primary/80 hover:text-primary transition-colors py-1.5 px-4 bg-primary/5 rounded-md border border-primary/20 w-auto"
                        >
                            {isOpen ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
                            <span>View sub agent reasoning</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="overflow-hidden">
                            <div className="mt-2 border border-[#1D2739] rounded-lg overflow-hidden">
                                {isOpen && chatIdRef.current && (
                                    <div className="bg-[#0A0F1E] h-[500px] overflow-hidden pb-1 px-1">
                                        <div className="relative h-full overflow-hidden rounded-md">
                                            <ChatMessageStream
                                                chatId={chatIdRef.current}
                                                maxHeight="100%"
                                                autoScroll={false}
                                                className="h-full"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </div>
            )}
        </div>
    );
}

// SubAgentResponse component
function SubAgentResponse({ message, chatIdRef }: { message: Message, chatIdRef: React.RefObject<string | null> }) {
    const [isResultOpen, setIsResultOpen] = useState(true);

    // Extract the result from the tool response
    let result = "No response received yet";
    if (message.tool_call_response_content) {
        try {
            const response = JSON.parse(message.tool_call_response_content);
            if (response && response.result) {
                if (typeof response.result === 'string') {
                    result = response.result;
                } else {
                    result = JSON.stringify(response.result, null, 2);
                }
            } else {
                // If there's no result field, use the whole response as the result
                result = JSON.stringify(response, null, 2);
            }
        } catch (error) {
            console.error("Error parsing tool response:", error);
            result = message.tool_call_response_content;
        }
    }

    // Format the agent name for display
    let agentDisplayName = "Sub Agent";
    if (message.tool_name) {
        agentDisplayName = message.tool_name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') + " Sub Agent";
    }

    return (
        <div className="space-y-4 w-full">
            {/* Content section with subtle border */}
            <div className="">
                <div className="text-textmedium">
                    <MarkdownRenderer content={message.content || ''} />
                </div>
            </div>

            {/* Agent header with icon */}
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <CornerDownRight className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-primary font-medium">{agentDisplayName} Result</span>
                    <span className="text-green-500 text-xs px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20">Completed</span>
                </div>
            </div>

            {/* Result in collapsible section */}
            <div className="mt-2">
                <Collapsible open={isResultOpen} onOpenChange={setIsResultOpen} className="w-full">
                    <CollapsibleTrigger 
                        className="flex items-center gap-2 text-sm text-primary/80 hover:text-primary transition-colors py-1.5 px-4 bg-primary/5 rounded-md border border-primary/20 w-auto"
                    >
                        {isResultOpen ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
                        <span>Sub agent result</span>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="overflow-hidden">
                        <div className="mt-2 border border-[#1D2739] rounded-lg overflow-hidden p-4 bg-[#1D2739]/20">
                            <div className="text-textmedium">
                                <MarkdownRenderer content={result} />
                            </div>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </div>
        </div>
    );
}

// Main tool renderer for subagent calls
export function SubAgentToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // Initialize chatIdRef with the appropriate ID immediately
    const initialChatId = message.tool_call_id || message.tool_call_response_id || null;
    const chatIdRef = useRef<string | null>(initialChatId);

    // Extract the chat ID and update if it changes
    useEffect(() => {
        // The most important case: use the tool_call_id
        if (message.tool_call_id) {
            chatIdRef.current = message.tool_call_id;
            console.log(`SubAgentToolRenderer: Using chat ID from tool_call_id: ${message.tool_call_id}`);
        }
        // For responses, use the tool_call_response_id
        else if (message.tool_call_response_id) {
            chatIdRef.current = message.tool_call_response_id;
            console.log(`SubAgentToolRenderer: Using chat ID from tool_call_response_id: ${message.tool_call_response_id}`);
        }
    }, [message]);

    // If this is a standard tool call without custom handling, use default renderer
    if (isToolCall && !message.tool_call_function_name) {
        return <DefaultToolCallRenderer message={message} />;
    }

    // Render the appropriate component based on whether it's a tool call or response
    return message.tool_call_function_name ? 
        <SubAgentToolCall message={message} chatIdRef={chatIdRef} /> :
        <SubAgentResponse message={message} chatIdRef={chatIdRef} />;
}

// Static method to determine if this renderer should render a message
SubAgentToolRenderer.shouldRender = (message: Message): boolean => {
    // List of all sub-agent tool names
    const subAgentToolNames = [
        // Core agents
        "log_querier",
        "metric_querier", 
        "trace_querier",
        "coder",
        "profile_querier",
        "kubernetes_resource_querier",
        "guardian"
    ];

    // Check for tool calls - when tool_call_function_name is present
    if (message.tool_call_function_name && subAgentToolNames.includes(message.tool_call_function_name)) {
        return true;
    }

    // Check for tool responses - when tool_name is present or there's a tool_call_response_id
    if ((message.tool_name && subAgentToolNames.includes(message.tool_name)) || 
        (message.tool_call_response_id && !message.tool_call_function_name)) {
        return true;
    }

    return false;
}; 
